import { Resolution } from 'src/models/resolution/resolution';
import { Entity } from 'src/models/entity/entity';
import { makeKeyValueRepository } from 'src/data/repositories/key-value/key-value-repository-factory';
import { RepositoryKey } from 'src/models/common/repository/repository-key';
import { makeConfigService } from '../../common/config/config-service-factory';
import { CustomError } from '../../../../helpers/error/custom-error';
import { ResponsePagination } from '../../../../models/common/response/response-pagination/response-pagination';
import { BaseCrudServiceImpl } from '../../common/base-crud/base-crud-service-impl';
import { ILogService } from '../../common/log/log-service';
import { IAuthService } from '../../auth/auth-service';
import { IKeyParams } from 'src/models/common/params/key-params';
import { IdStatus } from 'src/models/common/base/id';
import { RESOLUTION_ENDPOINTS } from './resolution-endpoints';
import {
  EntityResolutionLink,
  IResolutionService,
  SyncEntityResolutionsPayload,
} from './resolution-service';
import { SimilarNameCheckResult } from '../common/similar-name-check';
import { makeLoadEntityFromCentralUseCase } from 'src/usecases/central/entity/load-entity-from-central/load-entity-from-central-use-case-factory';
import { LanguageKey } from 'src/main/translate/language-key/language-key';
import {
  isEntitySharingRoot,
  resolveEntitySharingRoot,
} from 'src/helpers/entity/entity-sharing';

enum ResolutionSearchKey {
  NAME = 'name',
  PAGE = 'page',
  LIMIT = 'limit',
  SORT = 'sort',
  DIRECTION = 'direction',
  ALL = 'all',
  STATUS = 'status',
  TARGET_ENTITY_ID = 'target_entity_id',
  ONLY_LINKED = 'only_linked',
}

export class ResolutionServiceImpl
  extends BaseCrudServiceImpl<Resolution>
  implements IResolutionService
{
  private readonly keyValueRepository = makeKeyValueRepository();
  private readonly configService = makeConfigService();
  private readonly loadEntitiesUseCase = makeLoadEntityFromCentralUseCase();

  constructor(
    private resolutionLogService: ILogService,
    authService: IAuthService,
  ) {
    super(
      RESOLUTION_ENDPOINTS.base,
      Resolution.fromJson,
      resolutionLogService,
      authService,
    );
  }

  private async getSelectedEntity(): Promise<Entity | null> {
    const selectedEntity = await this.keyValueRepository.get(
      RepositoryKey.SELECTED_ENTITY,
    );

    return selectedEntity ? (selectedEntity as Entity) : null;
  }

  private isRootEntity(entity: Entity | null | undefined): boolean {
    return isEntitySharingRoot(
      entity,
      (taxId) => this.configService.isPrincipalEntityTaxId(taxId),
    );
  }

  private async assertRootEntityAccess(): Promise<Entity> {
    const selectedEntity = await this.getSelectedEntity();

    if (!this.isRootEntity(selectedEntity)) {
      throw CustomError.invalidContent([
        LanguageKey.ONLY_ROOT_ENTITY_CAN_MANAGE_RESOLUTIONS,
      ]);
    }

    return selectedEntity as Entity;
  }

  private async resolveCreationContext(): Promise<{
    selectedEntity: Entity;
    ownerRootEntity: Entity;
  }> {
    const selectedEntity = await this.getSelectedEntity();

    if (!selectedEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.NO_SELECTED_ENTITY_TO_CREATE_RESOLUTIONS,
      ]);
    }

    const isRootSelection = this.isRootEntity(selectedEntity);
    if (isRootSelection) {
      return {
        selectedEntity,
        ownerRootEntity: selectedEntity,
      };
    }

    const entities = await this.loadEntitiesUseCase.handle();
    const ownerRootEntity = resolveEntitySharingRoot(
      entities,
      selectedEntity.id,
      (taxId) => this.configService.isPrincipalEntityTaxId(taxId),
    );

    if (!ownerRootEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.COULD_NOT_LOCATE_CONFIGURED_ROOT_ENTITY,
      ]);
    }

    return {
      selectedEntity,
      ownerRootEntity,
    };
  }

  private async appendVisibilityParams(
    params: Record<string, string>,
  ): Promise<Record<string, string>> {
    const selectedEntity = await this.getSelectedEntity();

    if (selectedEntity?.id && !this.isRootEntity(selectedEntity)) {
      return {
        ...params,
        [ResolutionSearchKey.TARGET_ENTITY_ID]: selectedEntity.id,
        [ResolutionSearchKey.ONLY_LINKED]: 'true',
      };
    }

    return params;
  }

  override async get(id: string): Promise<Resolution> {
    try {
      await this.logService.info('ResolutionServiceImpl.get -> start');

      const params = await this.appendVisibilityParams({});
      const result = await this.authService.http.get(
        RESOLUTION_ENDPOINTS.byId(id),
        {
          params,
        },
      );

      return Resolution.fromJson(result);
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.get -> error',
        error,
      );
    } finally {
      await this.logService.info('ResolutionServiceImpl.get -> end');
    }
  }

  override async search(
    keyword: string,
    page: number,
    limit: number,
    all?: boolean,
    status?: IdStatus,
    direction?: 'asc' | 'desc',
  ): Promise<ResponsePagination> {
    const safeKeyword = keyword?.trim() || '';
    const keys: IKeyParams<ResolutionSearchKey>[] = [];

    if (safeKeyword.length > 0) {
      keys.push({ key: ResolutionSearchKey.NAME, value: safeKeyword });
    }

    keys.push({ key: ResolutionSearchKey.PAGE, value: (page || 1).toString() });
    keys.push({
      key: ResolutionSearchKey.LIMIT,
      value: (limit || 10).toString(),
    });
    keys.push({
      key: ResolutionSearchKey.DIRECTION,
      value: direction || 'asc',
    });

    if (all) {
      keys.push({ key: ResolutionSearchKey.ALL, value: 'true' });
    }
    if (status) {
      keys.push({ key: ResolutionSearchKey.STATUS, value: status });
    }

    return this.searchByParams(keys);
  }

  async searchByParams(
    keys: IKeyParams<ResolutionSearchKey>[],
  ): Promise<ResponsePagination> {
    try {
      await this.logService.info('ResolutionServiceImpl.search -> start');

      const baseParams = keys.reduce(
        (acc, { key, value }) => {
          acc[key] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
      const params = await this.appendVisibilityParams(baseParams);

      const queryString = new URLSearchParams(
        Object.entries(params).reduce(
          (acc, [key, value]) => {
            acc[key] = String(value);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString();

      const result = await this.authService.http.get(
        `${this.apiPath}?${queryString}`,
      );

      return ResponsePagination.fromJson(result);
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.search -> error',
        error,
      );
    } finally {
      await this.logService.info('ResolutionServiceImpl.search -> end');
    }
  }

  override async create(data: Resolution): Promise<string> {
    try {
      await this.logService.info('ResolutionServiceImpl.create -> start');
      const creationContext = await this.resolveCreationContext();

      const result = await this.authService.http.post(`${this.apiPath}`, {
        ...data,
        owner_root_entity_id:
          data?.owner_root_entity_id || creationContext.ownerRootEntity.id,
        target_entity_id: creationContext.selectedEntity.id,
      });
      return result.id;
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.create -> error',
        error,
      );
    } finally {
      await this.logService.info('ResolutionServiceImpl.create -> end');
    }
  }

  override async update(id: string, data: Resolution): Promise<void> {
    try {
      await this.logService.info('ResolutionServiceImpl.update -> start');
      const selectedRootEntity = await this.assertRootEntityAccess();

      await this.authService.http.put(RESOLUTION_ENDPOINTS.byId(id), {
        ...data,
        owner_root_entity_id:
          data?.owner_root_entity_id || selectedRootEntity.id,
      });
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.update -> error',
        error,
      );
    } finally {
      await this.logService.info('ResolutionServiceImpl.update -> end');
    }
  }

  override async delete(id: string): Promise<void> {
    try {
      await this.logService.info('ResolutionServiceImpl.delete -> start');
      await this.assertRootEntityAccess();

      await this.authService.http.delete(RESOLUTION_ENDPOINTS.byId(id));
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.delete -> error',
        error,
      );
    } finally {
      await this.logService.info('ResolutionServiceImpl.delete -> end');
    }
  }

  async checkSimilarName(
    name: string,
    excludeId?: string,
  ): Promise<SimilarNameCheckResult> {
    try {
      await this.logService.info(
        'ResolutionServiceImpl.checkSimilarName -> start',
      );
      const creationContext = await this.resolveCreationContext();

      return await this.authService.http.post(
        RESOLUTION_ENDPOINTS.checkSimilarName,
        {
          name,
          exclude_id: excludeId || undefined,
          owner_root_entity_id: creationContext.ownerRootEntity.id,
        },
      );
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.checkSimilarName -> error',
        error,
      );
    } finally {
      await this.logService.info(
        'ResolutionServiceImpl.checkSimilarName -> end',
      );
    }
  }

  async listEntityLinks(entityId: string): Promise<EntityResolutionLink[]> {
    try {
      await this.logService.info(
        'ResolutionServiceImpl.listEntityLinks -> start',
      );
      await this.assertRootEntityAccess();

      const result = await this.authService.http.get(
        RESOLUTION_ENDPOINTS.entityLinks(entityId),
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.listEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info(
        'ResolutionServiceImpl.listEntityLinks -> end',
      );
    }
  }

  async syncEntityLinks(
    entityId: string,
    payload: SyncEntityResolutionsPayload,
  ): Promise<EntityResolutionLink[]> {
    try {
      await this.logService.info(
        'ResolutionServiceImpl.syncEntityLinks -> start',
      );
      const selectedRootEntity = await this.assertRootEntityAccess();

      const result = await this.authService.http.put(
        RESOLUTION_ENDPOINTS.entityLinks(entityId),
        {
          ...payload,
          owner_root_entity_id:
            payload.owner_root_entity_id || selectedRootEntity.id,
        },
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'ResolutionServiceImpl.syncEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info(
        'ResolutionServiceImpl.syncEntityLinks -> end',
      );
    }
  }
}
